import React from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { clearNotification, showNotification } from '../../store/notificationSlice';

const ICON_CLASS = 'h-5 w-5';
const iconMap = {
  success: <CheckCircleIcon className={ICON_CLASS} style={{ color: '#28a745' }} />,
  error: <ExclamationCircleIcon className={ICON_CLASS} style={{ color: '#dc3545' }} />,
  warning: <ExclamationTriangleIcon className={ICON_CLASS} style={{ color: '#ffc107' }} />,
  info: <InformationCircleIcon className={ICON_CLASS} style={{ color: '#0061B2' }} />,
};

const ACCENT = {
  success: '#28a745',
  error: '#dc3545',
  warning: '#ffc107',
  info: '#0061B2',
};

// Sanitize Frappe-style HTML messages. Backend sends things like
//   "Row #1: Item <strong>PD-002</strong> has no stock in <strong>X</strong>"
// We escape everything, then re-allow a small whitelist so <strong>, <br>,
// etc. render properly without exposing the app to injected scripts.
const ALLOWED_TAG = new Set([
  'strong', 'b', 'em', 'i', 'br', 'p', 'ul', 'ol', 'li', 'code', 'small',
]);
const sanitizeHtml = (input) => {
  if (input == null) return '';
  const stripped = String(input).replace(
    /<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\s*\1\s*>/gi,
    '',
  );
  return stripped
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/&lt;(\/?)([a-zA-Z0-9]+)(?:\s[^&]*?)?&gt;/g, (match, slash, tag) => {
      const lc = tag.toLowerCase();
      return ALLOWED_TAG.has(lc) ? `<${slash}${lc}>` : match;
    });
};

const NotificationProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const { open, message, severity = 'info', title, duration } =
    useAppSelector((state) => state.notification);

  // Listen for global API error events emitted from the axios interceptor
  React.useEffect(() => {
    const handler = (event) => {
      if (event?.detail) {
        dispatch(showNotification(event.detail));
      }
    };
    window.addEventListener('global-api-error', handler);
    return () => window.removeEventListener('global-api-error', handler);
  }, [dispatch]);

  // Errors and warnings get a longer dwell so the user can actually read
  // multi-line messages (e.g. a stock error with item codes + warehouse).
  const effectiveDuration =
    duration ??
    (severity === 'error' ? 8000 : severity === 'warning' ? 6000 : 3000);

  // Auto-hide (replaces MUI Snackbar autoHideDuration). Restarts whenever a
  // new notification is shown.
  React.useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      dispatch(clearNotification());
    }, effectiveDuration);
    return () => clearTimeout(timer);
  }, [open, message, title, severity, effectiveDuration, dispatch]);

  const accent = ACCENT[severity] || ACCENT.info;
  const sanitizedMessage = sanitizeHtml(message);
  const sanitizedTitle = sanitizeHtml(title);

  return (
    <>
      {children}
      {open && (
        <div className="fixed right-2 top-16 z-[1400] sm:right-4">
          <div
            role="alert"
            className="flex w-[calc(100vw-32px)] max-w-[min(420px,calc(100vw-32px))] items-start gap-2.5 rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-[#1f2937] shadow-[0_10px_24px_rgba(0,0,0,0.12),0_2px_6px_rgba(0,0,0,0.06)] sm:w-[min(420px,calc(100vw-32px))]"
            style={{ borderLeft: `4px solid ${accent}` }}
          >
            <div className="pt-0.5">{iconMap[severity] || iconMap.info}</div>
            <div className="min-w-0 flex-grow">
              {sanitizedTitle && (
                <p
                  className="mb-0.5 break-words text-sm font-bold"
                  style={{ color: accent }}
                  dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
                />
              )}
              <div
                className="whitespace-normal break-words text-[0.8125rem] leading-normal text-[#212B36] [&_b]:font-bold [&_b]:text-[#111827] [&_code]:rounded-sm [&_code]:bg-black/5 [&_code]:px-1 [&_code]:font-mono [&_code]:text-xs [&_p+p]:mt-1 [&_p]:m-0 [&_strong]:font-bold [&_strong]:text-[#111827]"
                dangerouslySetInnerHTML={{ __html: sanitizedMessage }}
              />
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dispatch(clearNotification())}
              className="-mr-1 -mt-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[#637381] hover:bg-slate-100 hover:text-[#212B36]"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationProvider;
